import AppVirtualTable from '../components/AppVirtualTable.vue';
import {
    getTableData,
    updateLookups,
    getLookupTypes,
    getHeaders,
    getCellComponent,
    buildCellOptions,
    applyForeignKeySorting
} from '@/features/schemas/services/tableService';
import { getQueryField } from '@/features/schemas/services/schemaProvider';
import { buildTableQuery } from '@/features/schemas/services/searcher';
import { equal, and } from '@/features/schemas/services/schemaFiltering';
import schemaMixin from '@/features/schemas/mixins/schemaMixin';
import { hasProperty } from '@/services/objectUtility';
import { isNullOrWhiteSpace, trim } from '@/services/stringUtility';
import { debounce } from '@/services/debounce';
import { subscribe } from '@/services/actionHub';

export default {
    components: {
        AppVirtualTable
    },
    mixins: [schemaMixin],
    props: {
        filter: {
            type: Object,
            default: null
        },
        fixedValues: {
            type: Object,
            default: null
        },
        hiddenFields: {
            type: Array,
            default: () => []
        },
        searchText: {
            type: String,
            default: ''
        },
        sortBy: {
            type: String,
            default: null
        },
        direction: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            // Virtual table specific data
            items: new Map(),
            totalItems: 0,
            totals: null,
            loadedRanges: [],
            tableData: {
                items: [],
                total: 0,
                totals: undefined,
                lookups: {},
                entityKey: null
            },
            currentSortBy: null,
            currentSortDesc: false,

            // From original mixin
            entityHeaders: [],
            hasExtraActions: false,
            subscription: null,
            loading: false,
            initialLoadComplete: false
        };
    },
    computed: {
        headers() {
            // Convert entity headers to virtual table format
            const baseHeaders = this.entityHeaders.map(h => {
                const field = getQueryField(this.entityKey, h.value);
                return {
                    text: h.text,
                    value: h.value,
                    sortable: h.sortable !== false,
                    align: h.class === 'text-right' ? 'right' :
                        h.class === 'text-center' ? 'center' : 'left',
                    width: h.width,
                    hasTotal: h.hasTotal === true,
                    formatter: h.formatter,
                    // Include field metadata for labeller
                    field: field,
                    // Include entityKey for foreign key fields
                    entityKey: field?.dependsOn?.entityKey
                };
            });

            if (!(this.canEdit || this.canView || this.hasExtraActions)) {
                return baseHeaders;
            }

            return baseHeaders.concat({
                text: '',
                value: 'actions',
                sortable: false,
                align: 'center',
                width: '140px'
            });
        },
        fieldKeys() {
            return this.entityHeaders.map(h => h.value);
        },
        fixedFields() {
            return Object.keys(this.fixedValues ?? {});
        },
        requestModel() {
            const fixedFilters = this.fixedFields
                .map(f => equal(this.entityKey, f, this.fixedValues[f]));

            const filters = [...fixedFilters, this.filter].filter(f => f != null);
            const filter = filters.length ? and(filters) : null;

            return { filter };
        }
    },
    watch: {
        entityKey: {
            immediate: true,
            handler() {
                this.updateHeaders();
                this.resetAndLoad();
            }
        },
        fixedValues: {
            deep: true,
            handler() {
                this.updateHeaders();
            }
        },
        hiddenFields: {
            deep: true,
            handler() {
                this.updateHeaders();
            }
        },
        requestModel: {
            deep: true,
            handler() {
                this.resetAndLoad();
            }
        },
        searchText: {
            immediate: false,
            handler() {
                this.debouncedSearch();
            }
        },
        sortBy: {
            immediate: true,
            handler(value) {
                if (value !== null) {
                    this.currentSortBy = value;
                }
            }
        },
        direction: {
            immediate: true,
            handler(value) {
                if (value !== null) {
                    this.currentSortDesc = value === 'descending';
                }
            }
        },
        currentSortBy: {
            handler(newValue, oldValue) {
                // Only reload if we have an entity key and the value actually changed
                if (this.entityKey && oldValue !== undefined && newValue !== oldValue) {
                    this.resetAndLoad();
                }
            }
        },
        currentSortDesc: {
            handler(newValue, oldValue) {
                // Only reload if we have an entity key and the value actually changed
                if (this.entityKey && oldValue !== undefined && newValue !== oldValue) {
                    this.resetAndLoad();
                }
            }
        }
    },
    mounted() {
        this.hasExtraActions = hasProperty(this.$scopedSlots, 'extra-actions');
        let entityKeys = [
            this.entityKey,
            ...getLookupTypes(this.entityKey)
        ];
        this.subscription = subscribe(this.receiveMessages, entityKeys);

        // Set initial sort
        if (this.sortBy) {
            this.currentSortBy = this.sortBy;
            this.currentSortDesc = this.direction === 'descending';
        }

        // Initial load if we haven't already from watchers
        if (this.totalItems === 0 && this.entityKey) {
            this.getInitialCount();
        }
    },
    destroyed() {
        this.subscription?.unsubscribe();
    },
    methods: {
        updateHeaders() {
            this.entityHeaders = this.entityKey ?
                getHeaders(this.entityKey, this.scope)
                    .filter(h => !this.fixedFields.includes(h.value)
                        && !this.hiddenFields.includes(h.value)) :
                [];
        },
        buildRequestModel(skip, take, excludeCount = true) {
            const model = {
                ...this.requestModel,
                skip,
                take,
                sortBy: this.currentSortBy,
                direction: this.currentSortDesc ? 'descending' : 'ascending',
                excludeCount
            };

            // Apply foreign key sorting if needed
            if (model.sortBy) {
                model.sortBy = applyForeignKeySorting(this.entityKey, model.sortBy);
            }

            if (!isNullOrWhiteSpace(trim(this.searchText, '"'))) {
                return buildTableQuery(this.entityKey, this.searchText, model);
            }

            return model;
        },
        getCellComponent(fieldKey) {
            return getCellComponent(this.entityKey, fieldKey);
        },
        getCellOptions(fieldKey) {
            // Ensure entityKey is always set for cell options
            const tableDataWithKey = {
                ...this.tableData,
                entityKey: this.entityKey
            };
            return buildCellOptions(tableDataWithKey, fieldKey);
        },
        async getInitialCount() {
            const countModel = this.buildRequestModel(0, 50, false);

            this.loading = true;
            try {
                // Fetch totals if any headers have hasTotal flag
                const excludeTotals = !this.headers.some(h => h.hasTotal === true);

                const result = await getTableData(this.entityKey, countModel, this.scope, excludeTotals);

                // Update tableData for cell options
                this.tableData = {
                    items: result.items || [],
                    total: result.total || 0,
                    lookups: result.lookups || {},
                    totals: result.totals,
                    entityKey: this.entityKey
                };

                // Only set totalItems after we're mounted and ready
                await this.$nextTick();
                this.totalItems = result.total || 0;

                // Update totals if they were fetched
                if (!excludeTotals && result.totals !== undefined) {
                    this.totals = result.totals;
                }

                // Update items from initial load
                if (result.items && result.items.length > 0) {
                    result.items.forEach((item, index) => {
                        this.items.set(index, item);
                    });
                    this.items = new Map(this.items);

                    // Update loaded ranges
                    this.updateLoadedRanges({ start: 0, end: result.items.length - 1 });

                    // Trigger column width recalculation after first data load
                    await this.$nextTick();
                    this.$emit('data-loaded');
                }

                if (this.totalItems === 0) {
                    this.initialLoadComplete = true;
                }
            } catch (error) {
                this.totalItems = 0;
                this.initialLoadComplete = true;
            } finally {
                this.loading = false;
            }
        },
        async loadRange(range) {
            const model = this.buildRequestModel(range.start, range.end - range.start + 1, true);

            this.loading = true;
            try {
                // Fetch totals only if we haven't fetched them yet
                const excludeTotals = this.totals !== null;

                const result = await getTableData(this.entityKey, model, this.scope, excludeTotals);

                // Update tableData for cell options - accumulate items and merge lookups
                this.tableData = {
                    items: [...(this.tableData.items || []), ...result.items],
                    total: result.total !== undefined ? result.total : this.tableData.total,
                    lookups: { ...(this.tableData.lookups || {}), ...(result.lookups || {}) },
                    totals: result.totals !== undefined ? result.totals : this.tableData.totals,
                    entityKey: this.entityKey
                };

                // Update totals only if they were fetched (not excluded)
                if (!excludeTotals && result.totals !== undefined) {
                    this.totals = result.totals;
                }

                // Clear and re-add items to ensure they're properly set
                result.items.forEach((item, index) => {
                    const itemIndex = range.start + index;
                    this.items.set(itemIndex, item);
                });

                // Force Vue to detect the change
                this.items = new Map(this.items);

                // Update loaded ranges
                this.updateLoadedRanges(range);

                // Emit list request for export functionality
                this.$emit('list-request', model);
            } finally {
                this.loading = false;
                this.initialLoadComplete = true;
            }
        },
        updateLoadedRanges(newRange) {
            const ranges = [...this.loadedRanges, newRange].sort((a, b) => a.start - b.start);
            const merged = [];

            let current = null;
            for (const range of ranges) {
                if (!current) {
                    current = { ...range };
                } else if (range.start <= current.end + 1) {
                    current.end = Math.max(current.end, range.end);
                } else {
                    merged.push(current);
                    current = { ...range };
                }
            }

            if (current) {
                merged.push(current);
            }

            this.loadedRanges = merged;
        },
        resetData() {
            // Don't clear items or totalItems immediately - keep them for visual continuity during reload
            // They will be replaced when new data arrives
            this.loadedRanges = [];
            this.totals = null;
            this.tableData.lookups = {};
            this.initialLoadComplete = false;
        },
        async resetAndLoad() {
            this.resetData();
            await this.$nextTick();
            await this.getInitialCount();
        },
        async refresh() {
            await this.resetAndLoad();
        },
        viewItem(item) {
            if (!this.canView) {
                return;
            }
            this.$router.push({
                name: this.entityKey,
                params: { itemId: item.id }
            });
        },
        async receiveMessages(messages) {
            // If this entity has changes, refresh
            if (messages.some(m => m.entityKey === this.entityKey)) {
                await this.refresh();
                return;
            }

            // Update lookups
            await updateLookups(this.tableData.lookups, messages);
        },
        async search() {
            await this.resetAndLoad();
        }
    },
    created() {
        this.debouncedSearch = debounce(() => this.search(), 250);
    }
};
